import { useState, useEffect, useCallback } from "react";
import { useCart, formatMoney } from "@/lib/cart";
import { fetchStarterKits } from "@/lib/api";
import { Package, Drop, ShoppingCart, CheckCircle, Sparkle, Info } from "@phosphor-icons/react";

/**
 * StarterKitsCard
 *
 * Shows a "Recommended Starter Kits (10% off)" card for a given compressor
 * product_type. Two kits are highlighted: the Break-In kit (one-off, ≤500 h)
 * and the 3,000-hour Service Kit (repeats). Oil lines are annotated with
 * 5-gallon bucket math. A single "Add both to cart" button drops every kit
 * line into the global cart with `discount: 0.10`, so the 10% applies to
 * kit lines only when the quote is created.
 */
export default function StarterKitsCard({ productType, brand = "US", shifts = 1, compressorPrice = 0 }) {
  const currency = brand === "ACC" ? "CAD" : "USD";
  const sym = currency === "CAD" ? "C$" : "$";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [justAdded, setJustAdded] = useState("");
  const { addItem } = useCart();

  const load = useCallback(async () => {
    if (!productType) return;
    setLoading(true); setError("");
    try {
      const d = await fetchStarterKits(productType, currency, shifts, 3);
      setData(d);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load starter kits");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [productType, currency, shifts]);

  useEffect(() => { load(); }, [load]);

  const addKit = useCallback((kit, kitLabel) => {
    if (!kit || !kit.items?.length) return;
    const discountFrac = (kit.discount_pct || 10) / 100;
    for (const it of kit.items) {
      // Oil: cart qty = buckets to BUY (since that's what's sold — 5-gal bucket).
      // Grease + other: cart qty = spec qty as-is.
      const qty = it.is_oil ? (it.oil_buckets_to_buy || 1) : (it.qty || 1);
      addItem(
        {
          product_code: it.product_code,
          product_name: it.part_name || it.product_code,
          price: it.unit_price,
          currency,
          discount: discountFrac,
          kit_label: kitLabel,
        },
        qty
      );
    }
    setJustAdded(kitLabel);
    setTimeout(() => setJustAdded(""), 2500);
  }, [addItem, currency]);

  const addBoth = useCallback(() => {
    if (data?.break_in) addKit(data.break_in, "break_in");
    if (data?.service_3000) addKit(data.service_3000, "service_3000");
    setJustAdded("both");
    setTimeout(() => setJustAdded(""), 2500);
  }, [data, addKit]);

  if (loading) {
    return (
      <div data-testid="starter-kits-loading" className="rounded-sm border border-emerald-200 bg-emerald-50/40 p-3 text-xs text-emerald-700">
        Loading starter-kit recommendation…
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="starter-kits-error" className="rounded-sm border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500">
        Starter kits unavailable for this model — {error}
      </div>
    );
  }
  if (!data || (!data.break_in && !data.service_3000)) return null;

  const discountPct = data.discount_pct ?? 10;
  const combinedSubtotal = data.combined_subtotal || 0;
  const combinedDiscount = data.combined_discount_amount || 0;
  const combinedFinal = data.combined_total_after_discount || 0;

  return (
    <div
      data-testid="starter-kits-card"
      className="rounded-sm border-2 border-emerald-200 bg-gradient-to-br from-emerald-50/80 to-white p-4 space-y-3"
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 bg-emerald-600 text-white rounded-full w-8 h-8 flex items-center justify-center">
          <Package size={16} weight="bold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-black text-slate-900">Recommended Starter Kits</h3>
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-600 text-white">
              <Sparkle size={10} weight="fill" /> {discountPct}% off
            </span>
          </div>
          <p className="text-[11px] text-slate-600 mt-0.5">
            Bundle your <b>Break-In kit</b> and first <b>3,000-hour service kit</b> with the compressor and save {discountPct}% on the kits only.
          </p>
        </div>
        {combinedSubtotal > 0 && (
          <div className="text-right flex-shrink-0">
            <div className="text-[10px] text-slate-500 font-bold uppercase">You save</div>
            <div className="text-base font-black text-emerald-700">{sym}{combinedDiscount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        )}
      </div>

      {/* The two kits side-by-side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <KitPanel
          kit={data.break_in}
          sym={sym}
          onAdd={() => addKit(data.break_in, "break_in")}
          added={justAdded === "break_in" || justAdded === "both"}
          testid="starter-kit-breakin"
          accent="blue"
          subtitle="One-time · fires at 500 running hours"
        />
        <KitPanel
          kit={data.service_3000}
          sym={sym}
          onAdd={() => addKit(data.service_3000, "service_3000")}
          added={justAdded === "service_3000" || justAdded === "both"}
          testid="starter-kit-service3000"
          accent="amber"
          subtitle="Repeats · first at 3,000 hours"
        />
      </div>

      {/* Oil forecast */}
      {data.oil_forecast?.enabled && data.oil_forecast.services?.length > 0 && (
        <div data-testid="starter-kits-oil-forecast" className="rounded-sm border border-slate-200 bg-white p-3">
          <div className="flex items-center gap-2 mb-2">
            <Drop size={13} weight="bold" className="text-blue-600" />
            <p className="text-xs font-bold text-slate-900">Oil bucket forecast — 5-gallon buckets</p>
          </div>
          <p className="text-[11px] text-slate-600 mb-2">
            Each 5-gallon bucket is used across services; leftover oil is carried forward before you need to buy a new one.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead className="text-slate-500 font-bold">
                <tr>
                  <th className="text-left px-2 py-1">Service</th>
                  <th className="text-right px-2 py-1">At hours</th>
                  <th className="text-right px-2 py-1">Oil needed</th>
                  <th className="text-right px-2 py-1">Buy buckets</th>
                  <th className="text-right px-2 py-1">Leftover after</th>
                </tr>
              </thead>
              <tbody>
                {data.oil_forecast.services.map((s, i) => (
                  <tr key={i} className={i % 2 ? "bg-white" : "bg-slate-50/60"}>
                    <td className="px-2 py-1 font-mono text-slate-700">#{s.service_num}</td>
                    <td className="px-2 py-1 text-right font-mono">{s.at_hours.toLocaleString()} h</td>
                    <td className="px-2 py-1 text-right">{s.gallons_needed.toFixed(1)} gal</td>
                    <td className={`px-2 py-1 text-right font-bold ${s.buckets_to_buy === 0 ? "text-emerald-700" : "text-slate-800"}`}>
                      {s.buckets_to_buy === 0 ? "✓ from leftover" : `${s.buckets_to_buy} × 5 gal`}
                    </td>
                    <td className="px-2 py-1 text-right font-mono text-slate-500">{s.leftover_after.toFixed(1)} gal</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-slate-500 mt-1.5">
            Total buckets over {Math.round(data.oil_forecast.horizon_hours / data.oil_forecast.hours_per_year)} year(s): <b>{data.oil_forecast.total_buckets_needed}</b>
          </p>
        </div>
      )}

      {/* Footer CTA */}
      <div className="flex items-center justify-between gap-3 flex-wrap pt-1 border-t border-emerald-100">
        <div className="text-[11px] text-slate-600 flex items-start gap-1.5">
          <Info size={12} weight="bold" className="text-blue-600 flex-shrink-0 mt-0.5" />
          <span>Discount applies to kit lines only. Added as discounted Quote_Detail rows when you checkout.</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="text-[10px] text-slate-500 font-bold uppercase">Bundled total</div>
            <div className="text-sm font-black text-slate-900">
              <span className="line-through text-slate-400 mr-1">{sym}{combinedSubtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              {sym}{combinedFinal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
          <button
            data-testid="starter-kits-add-both"
            onClick={addBoth}
            className="inline-flex items-center gap-1.5 h-10 px-4 text-xs font-bold rounded-sm text-white bg-emerald-700 hover:bg-emerald-800 disabled:bg-slate-300 whitespace-nowrap"
          >
            {justAdded === "both" ? (
              <><CheckCircle size={13} weight="bold" /> Added to cart</>
            ) : (
              <><ShoppingCart size={13} weight="bold" /> Add both to cart ({discountPct}% off)</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function KitPanel({ kit, sym, onAdd, added, testid, accent, subtitle }) {
  if (!kit) return null;
  const accentBorder = accent === "blue" ? "border-blue-200" : "border-amber-200";
  const accentBg = accent === "blue" ? "bg-blue-50" : "bg-amber-50";
  const accentText = accent === "blue" ? "text-blue-900" : "text-amber-900";
  return (
    <div data-testid={testid} className={`rounded-sm border ${accentBorder} bg-white p-3`}>
      <div className={`flex items-baseline justify-between gap-2 mb-2 pb-1.5 border-b ${accentBorder}`}>
        <div>
          <div className={`text-xs font-black ${accentText}`}>{kit.label}</div>
          <div className="text-[10px] text-slate-500">{subtitle}</div>
        </div>
        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${accentBg} ${accentText}`}>
          {kit.line_count} line{kit.line_count === 1 ? "" : "s"}
        </span>
      </div>
      <ul className="space-y-1 mb-2">
        {kit.items.map((it, i) => (
          <li key={i} className="flex items-start justify-between gap-2 text-[11px]">
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-800 truncate">
                {it.is_oil && <Drop size={10} weight="fill" className="inline text-blue-500 mr-0.5 -mt-0.5" />}
                {it.is_grease && <span className="inline-block align-middle mr-1 text-[9px] font-bold uppercase text-amber-600">GREASE</span>}
                {it.part_name || it.product_code}
                {" "}
                <span className="text-slate-400 font-normal">
                  × {it.qty}{it.unit_label ? ` ${it.unit_label}${it.qty === 1 ? "" : "s"}` : ""}
                </span>
              </div>
              {it.is_oil && (
                <div className="text-[10px] text-slate-500">
                  Buy {it.oil_buckets_to_buy} × 5-gal bucket{it.oil_buckets_to_buy === 1 ? "" : "s"}
                  {it.oil_leftover_gallons > 0 && <span className="text-emerald-600"> · {it.oil_leftover_gallons} gal leftover</span>}
                </div>
              )}
              {it.is_grease && (
                <div className="text-[10px] text-slate-500">
                  Sold as {it.qty} × tube{it.qty === 1 ? "" : "s"}
                </div>
              )}
            </div>
            <div className="text-right font-mono text-slate-700 flex-shrink-0">
              {sym}{it.line_total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-between gap-2 text-xs border-t border-slate-100 pt-1.5">
        <div className="text-slate-600">
          <span className="line-through text-slate-400 mr-1">{sym}{kit.subtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          <span className="font-black text-slate-900">{sym}{kit.total_after_discount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          <span className="text-[10px] text-emerald-600 ml-1.5 font-bold">−{kit.discount_pct}%</span>
        </div>
        <button
          data-testid={`${testid}-add`}
          onClick={onAdd}
          className="inline-flex items-center gap-1 h-7 px-2 text-[10px] font-bold rounded-sm text-white bg-slate-700 hover:bg-slate-800 whitespace-nowrap"
        >
          {added ? <><CheckCircle size={10} weight="bold" /> Added</> : <><ShoppingCart size={10} weight="bold" /> Add this kit</>}
        </button>
      </div>
    </div>
  );
}
